import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "motion/react";
import { MegaMenuFeaturedCard } from "@/features/navigation/components/MegaMenu/MegaMenuFeaturedCard";
import { MegaMenuListItem } from "@/features/navigation/components/MegaMenu/MegaMenuListItem";
import type { MegaMenuSubItem, MegaMenuFeaturedContent } from "@/features/navigation/components/MegaMenu/types/megaMenu.types";
import { cn } from "@/shared/utils/style.util";

type MegaMenuDropdownProps = {
  items: MegaMenuSubItem[]
  onItemClick?: () => void
  position: { top: number, left: number }
  featuredContent?: MegaMenuFeaturedContent
  columns?: 1 | 2 | 3
};

export const MegaMenuDropdown = ({
  items,
  onItemClick,
  position,
  featuredContent,
  columns: customColumns,
}: MegaMenuDropdownProps) => {
  // Auto-calculate columns if not explicitly provided
  const columns = customColumns ?? (items.length > 6 ? 2 : 1);

  // Calculate dropdown width based on columns and featured content
  const getDropdownWidth = () => {
    if (featuredContent && columns === 2) return "min-w-[600px] max-w-[700px]";
    if (featuredContent && columns === 3) return "min-w-[800px] max-w-[900px]";
    if (columns === 3) return "min-w-[600px] max-w-[700px]";
    if (columns === 2) return "min-w-[480px] max-w-[560px]";
    return "min-w-[280px] max-w-[320px]";
  };

  // Adjust position to prevent overflow
  const adjustedPosition = {
    top: position.top,
    left: Math.min(position.left, window.innerWidth - (featuredContent ? 720 : columns === 2 ? 580 : 340)),
  };

  const dropdown = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -10 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className={cn(
          "fixed bg-white dark:bg-gray-800",
          "border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl",
          "p-6 z-50",
          getDropdownWidth(),
        )}
        style={{
          top: `${adjustedPosition.top}px`,
          left: `${adjustedPosition.left}px`,
        }}
        role="menu"
        aria-orientation="vertical"
      >
        <div
          className={cn(
            "grid gap-3",
            featuredContent && columns === 2 && "grid-cols-[.75fr_1fr]",
            featuredContent && columns === 3 && "grid-cols-[.65fr_1fr_1fr]",
            !featuredContent && columns === 2 && "grid-cols-2",
            !featuredContent && columns === 3 && "grid-cols-3",
            columns === 1 && "grid-cols-1",
          )}
        >
          {/* Featured Card */}
          {featuredContent && (
            <MegaMenuFeaturedCard content={featuredContent} />
          )}

          {/* Menu Items */}
          {items.map((item) => (
            <MegaMenuListItem
              key={item.to}
              item={item}
              onItemClick={onItemClick}
            />
          ))}
        </div>

        {/* Bottom gradient for visual depth */}
        <div className={cn(
          "absolute inset-x-0 bottom-0 h-px",
          "bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent",
        )}
        />
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(dropdown, document.body);
};
